<script lang="ts">
	import Modal from '$lib/Modal.svelte';
	import { filterEvenIndexedElements, filterOddIndexedElements, isMobileDevice } from '$lib/common';
	import { isMaybeAnswerEnabled } from '$lib/commonLogic';
	import { currentEvent } from '$lib/currentEvent';
	import { Attending } from '$lib/eventTypes';
	import { showGuestListEdit } from './GuestListEdit.svelte';
	import GuestListEntry from './GuestListEntry.svelte';
	import * as m from '$lib/paraglide/messages.js';

	interface Props {
		open?: boolean;
	}

	let { open = $bindable(false) }: Props = $props();

	let guests = $derived(
		$currentEvent.guests.sort((a, b) => {
			// sort order: avatar defined, authUser defined, everyone
			if (a.user?.avatarUrl && b.user?.avatarUrl) return 0;
			if (a.user?.avatarUrl) return -1;
			if (b.user?.avatarUrl) return 1;
			if (a.user?.authUserId && b.user?.authUserId) return 0;
			if (a.user?.authUserId) return -1;
			if (b.user?.authUserId) return 1;
			return 0;
		})
	);

	let guestsAttending = $derived(guests.filter((x) => x.attending === Attending.ATTENDING));
	let guestsMaybeAttending = $derived(guests.filter((x) => x.attending === Attending.MAYBE));
	let guestsNotAttending = $derived(guests.filter((x) => x.attending === Attending.NOT_ATTENDING));
	let guestsOnWaitlist = $derived(guests.filter((x) => x.attending === Attending.WAITLIST));

	function countGuestsWithCompanions(attending: Attending) {
		return guests
			.filter((x) => x.attending === attending)
			.map((x) => x.companions.length + 1)
			.reduce((a, b) => a + b, 0);
	}
</script>

{#if open}
	<Modal
		title={$currentEvent.isPublic ? m.guestList_title() : m.guestList_titlePrivate()}
		onclose={() => (open = false)}
	>
		{#if guests.length === 0}
			<div class="mb-6 mt-4 text-center text-lg">
				{@html m.guestList_noGuestsYet()}
				<div class="mt-2 text-3xl">😊</div>
			</div>
		{:else}
			<div class="mb-1 min-w-96">
				<div class="" data-testid="attending-guests">
					<div class="flex" class:mt-2={$currentEvent.ownerSecret}>
						<h4 class="label">
							{#if $currentEvent.isPublic}
								{m.guestList_registered()}
							{:else}
								{m.guestList_accepted()}
							{/if}
							<span class="font-normal not-italic"> ({countGuestsWithCompanions(Attending.ATTENDING)}) </span>
						</h4>
						{#if $currentEvent.ownerSecret}
							<button onclick={() => ($showGuestListEdit = true)} class="btn btn-secondary mx-6 -mt-4">
								{m.guestList_edit()}
							</button>
						{/if}
					</div>

					<hr class="mt-2 border-violet-200" />
					{#if guestsAttending.length === 0}
						<div class="pl-1 font-semibold text-gray-600">–</div>
					{/if}
					<div class="flex flex-wrap">
						{#if isMobileDevice()}
							<ul class="mr-4 mt-3 flex flex-col gap-4">
								{#each guestsAttending as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{:else}
							<ul class="mr-4 mt-3 flex flex-col gap-4 md:w-[48%]">
								{#each filterEvenIndexedElements(guestsAttending) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
							<ul class="mt-3 flex flex-col gap-4">
								{#each filterOddIndexedElements(guestsAttending) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{/if}
					</div>
				</div>

				{#if isMaybeAnswerEnabled($currentEvent)}
					<div class="mt-6" data-testid="maybe-guests" hidden={$currentEvent.isPublic}>
						<h4 class="label">
							{m.guestList_maybe()}
							<span class="font-normal not-italic leading-tight"> ({countGuestsWithCompanions(Attending.MAYBE)}) </span>
						</h4>
						<hr class="mt-2 border-violet-200" />
						{#if guestsMaybeAttending.length === 0}
							<div class="pl-1 font-semibold text-gray-600">–</div>
						{/if}
						<div class="flex flex-wrap">
							{#if isMobileDevice()}
								<ul class="mr-4 mt-3 flex flex-col gap-4">
									{#each guestsMaybeAttending as guest}
										<GuestListEntry {guest} />
									{/each}
								</ul>
							{:else}
								<ul class="mr-4 mt-3 flex flex-col gap-4 md:w-[48%]">
									{#each filterEvenIndexedElements(guestsMaybeAttending) as guest}
										<GuestListEntry {guest} />
									{/each}
								</ul>
								<ul class="">
									{#each filterOddIndexedElements(guestsMaybeAttending) as guest}
										<GuestListEntry {guest} />
									{/each}
								</ul>
							{/if}
						</div>
					</div>
				{/if}

				<div class="mt-6" data-testid="notAttending-guests" hidden={$currentEvent.isPublic}>
					<h4 class="label">
						{m.guestList_declined()}
						<span class="font-normal not-italic"> ({countGuestsWithCompanions(Attending.NOT_ATTENDING)}) </span>
					</h4>
					<hr class="mt-2 border-violet-200" />
					{#if guestsNotAttending.length === 0}
						<div class="pl-1 font-semibold text-gray-600">–</div>
					{/if}
					<div class="flex flex-wrap">
						{#if isMobileDevice()}
							<ul class="mr-4 mt-3 flex flex-col gap-4">
								{#each guestsNotAttending as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{:else}
							<ul class="mr-4 mt-3 flex flex-col gap-4 md:w-[48%]">
								{#each filterEvenIndexedElements(guestsNotAttending) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
							<ul class="">
								{#each filterOddIndexedElements(guestsNotAttending) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{/if}
					</div>
				</div>

				<div class="mt-6" data-testid="notAttending-guests" hidden={!$currentEvent.maxGuests}>
					<h4 class="label">
						{m.guestList_waitlist()}
						<span class="font-normal not-italic"> ({countGuestsWithCompanions(Attending.WAITLIST)}) </span>
					</h4>
					<hr class="mt-2 border-violet-200" />
					{#if guestsOnWaitlist.length === 0}
						<div class="pl-1 font-semibold text-gray-600">–</div>
					{/if}
					<div class="flex flex-wrap">
						{#if isMobileDevice()}
							<ul class="mr-4 mt-3 flex flex-col gap-4">
								{#each guestsOnWaitlist as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{:else}
							<ul class="mr-4 mt-3 flex flex-col gap-4 md:w-[48%]">
								{#each filterEvenIndexedElements(guestsOnWaitlist) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
							<ul class="">
								{#each filterOddIndexedElements(guestsOnWaitlist) as guest}
									<GuestListEntry {guest} />
								{/each}
							</ul>
						{/if}
					</div>
				</div>
			</div>
		{/if}
	</Modal>
{/if}
